import { HeartIcon } from "@heroicons/react/24/outline";
import { useQuery } from "@apollo/client";
import { OutfitData } from "@/types/Profile";
import OutfitCard from "@/components/outfits/OutfitCard";
import { useFavoriteOutfit } from "@/hooks/useFavoriteOutfit";
import { GET_OUTFITS_QUERY } from "@/graphql/queries";

interface FavouritesTabProps {}

export default function FavouritesTab({}: FavouritesTabProps) {
  const { toggleFavorite, loading: favoriteLoading } = useFavoriteOutfit();
  const { data, loading, error } = useQuery(GET_OUTFITS_QUERY);

  const outfits = data?.getCurrentUser?.outfits || [];
  const favouriteOutfits = outfits.filter((outfit: OutfitData) => outfit.favorited);

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="text-center">
          <h2 className="text-2xl font-extralight tracking-[0.3em] uppercase text-gray-900 dark:text-white">
            CURATED FAVOURITES
          </h2>
          <p className="mt-2 text-sm font-light tracking-wider text-gray-600 dark:text-gray-400">
            Your most cherished ensembles
          </p>
        </div>
        <div className="text-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-white mx-auto"></div>
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading favourites...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-8">
        <div className="text-center">
          <h2 className="text-2xl font-extralight tracking-[0.3em] uppercase text-gray-900 dark:text-white">
            CURATED FAVOURITES
          </h2>
          <p className="mt-2 text-sm font-light tracking-wider text-gray-600 dark:text-gray-400">
            Your most cherished ensembles
          </p>
        </div>
        <div className="text-center py-16">
          <p className="text-red-500 dark:text-red-400">Error loading favourites: {error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-2xl font-extralight tracking-[0.3em] uppercase text-gray-900 dark:text-white">
          CURATED FAVOURITES
        </h2>
        <p className="mt-2 text-sm font-light tracking-wider text-gray-600 dark:text-gray-400">
          Your most cherished ensembles
        </p>
      </div>

      {favouriteOutfits.length === 0 ? (
        <div className="text-center py-16">
          <HeartIcon className="mx-auto h-16 w-16 text-gray-300 dark:text-gray-600" />
          <h3 className="mt-4 text-lg font-light tracking-wider text-gray-500 dark:text-gray-400">
            NO FAVOURITES YET
          </h3>
          <p className="mt-2 text-sm text-gray-400 dark:text-gray-500">
            Heart outfits to see them here
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {favouriteOutfits.map((outfit: OutfitData) => (
            <OutfitCard
              key={outfit.id}
              id={outfit.id}
              occasion={outfit.occasion}
              imageUrl={outfit.imageUrl}
              outfitItems={outfit.outfitItems}
              favorited={outfit.favorited}
              onFavoriteToggle={toggleFavorite}
              createdAt={outfit.createdAt}
              updatedAt={outfit.updatedAt}
            />
          ))}
        </div>
      )}
    </div>
  );
}
